import { NonEmptyArray } from "@/src/types";
import { Resource, State } from "@/src/modeler/state";

//#Source https://bit.ly/2neWfJ2
export const toSnakeCase = (str: string) =>
  (
    str.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
    ) || []
  )
    .map((x) => x.toLowerCase())
    .join("_");

// Redefining here since the Wasm serialization is distinct from serde and
// it's nice to keep all modeling stuff separate for now.
export type FactArg = boolean | number | string | { tag: string; id: string };

export type Fact = {
  comment?: string;
  name: string;
  args: FactArg[];
};

export type TestAssertion = {
  expected: boolean;
  fact: Fact;
};

export type TestBlock = {
  comment?: string;
  name: string;
  setup: Fact[];
  assertions: TestAssertion[];
};

export const EMPTY_TEST_BLOCK: TestBlock = {
  name: "",
  setup: [],
  assertions: [],
};

// Translate JS args to Rust/Wasm ConcreteValues
export const factToWasm = ({ name, args }: Fact) => ({
  name,
  args: args.map((a) => {
    if (typeof a === "boolean") {
      return { Boolean: a };
    } else if (typeof a === "number") {
      return { Number: { Integer: a } };
    } else if (typeof a === "string") {
      return { String: a };
    } else {
      switch (a.tag) {
        case "Boolean":
          return { Boolean: a.id };
        case "Integer":
          return { Number: { Integer: a.id } };
        case "String":
          return { String: a.id };
        default:
          return { Literal: a };
      }
    }
  }),
});

export const factToString = (fact: Fact): string => {
  let s = `${fact.name}(`;
  fact.args.forEach((a, i) => {
    // Comma-separated
    if (i > 0) s += ", ";
    if (typeof a === "boolean" || typeof a === "number") {
      // Stringify booleans and numbers directly.
      s += a;
    } else if (typeof a === "string") {
      // Add double quotes to strings.
      s += `"${a}"`;
    } else {
      switch (a.tag) {
        // Stringify booleans and numbers directly.
        case "Integer":
        case "Boolean":
          s += a.id;
          break;
        // Add double quotes to strings.
        case "String":
          s += `"${a.id}"`;
          break;
        // Format literals.
        default:
          s += `${a.tag}{"${a.id}"}`;
      }
    }
  });
  s += ")";
  return s;
};

function testToString(t: TestBlock | null): string {
  if (!t) return "";
  return [
    ...(t.comment ? [t.comment] : []),
    `test "${t.name}" {`,
    `  setup {`,
    ...t.setup.map((fact) => {
      let s = "";
      if (fact.comment) s += `    ${fact.comment}\n`;
      s += `    ${factToString(fact)};`;
      return s;
    }),
    `  }`,
    ...t.assertions.map(({ expected, fact }) => {
      let s = "";
      if (fact.comment) s += `  ${fact.comment}\n`;
      s += `  ${expected ? "assert" : "assert_not"} ${factToString(fact)};`;
      return s;
    }),
    `}`,
  ].join("\n");
}

export function roleInheritanceTest(
  state: State,
  resource: Resource
): TestBlock | null {
  function testRoleAndPermissions(): {
    role: string;
    allowedPermission: string;
    notAllowedPermission: string;
  } | null {
    for (const role of resource.roles) {
      for (const allowedPermission of resource.permissions) {
        for (const notAllowedPermission of resource.permissions)
          if (
            resource.rolePermissionImplications.some(
              (implication) =>
                implication.permission === allowedPermission &&
                implication.role === role
            ) &&
            !resource.rolePermissionImplications.some(
              (implication) =>
                implication.permission === notAllowedPermission &&
                implication.role === role
            )
          ) {
            return { role, allowedPermission, notAllowedPermission };
          }
      }
    }
    return null;
  }
  const testRoleAndPermissionsResult = testRoleAndPermissions();
  if (testRoleAndPermissionsResult === null) {
    return null;
  }
  const { role, allowedPermission, notAllowedPermission } =
    testRoleAndPermissionsResult;
  function setupFactsFromAncestor(
    ancestor: Resource,
    height: number
  ): NonEmptyArray<Fact> {
    if (ancestor.isGrantedAccessTo) {
      return [
        {
          name: "has_role",
          args: [
            { tag: "User", id: "alice" },
            role,
            { tag: ancestor.name, id: "" + height },
          ],
        },
      ];
    } else {
      const nextAncestorName = ancestor.relations[0].resource;
      const nextAncestor = state.resources.find(
        (resource) => resource.name === nextAncestorName
      );
      if (nextAncestor === undefined) {
        throw new Error(
          `${ancestor.name}'s related resource ${nextAncestorName} was not found`
        );
      }
      return [
        ...setupFactsFromAncestor(nextAncestor, height + 1),
        {
          name: "has_relation",
          args: [
            { tag: ancestor.name, id: "" + height },
            toSnakeCase(nextAncestorName),
            { tag: nextAncestorName, id: "" + (height + 1) },
          ],
        },
      ];
    }
  }
  const setup = setupFactsFromAncestor(resource, 0);
  return {
    name: `${resource.name} - Permissions`,
    setup,
    assertions: [
      {
        expected: true,
        fact: {
          name: "allow",
          args: [
            { tag: "User", id: "alice" },
            allowedPermission,
            { tag: resource.name, id: "0" },
          ],
        },
      },
      {
        expected: false,
        fact: {
          name: "allow",
          args: [
            { tag: "User", id: "alice" },
            notAllowedPermission,
            { tag: resource.name, id: "0" },
          ],
        },
      },
    ],
  };
}

export function shareWithOtherUsersTest(resource: Resource): TestBlock | null {
  const implication = resource.rolePermissionImplications.find((implication) =>
    resource.sharing.canShareWithOtherUsers?.includes(implication.role)
  );
  if (implication === undefined) return null;
  const { role, permission } = implication;
  return {
    name: `${resource.name} - Sharing with other users`,
    setup: [
      {
        comment: `# Alice has been shared the ${role} role on this ${resource.name}`,
        name: "has_role",
        args: [
          { tag: "User", id: "alice" },
          role,
          { tag: resource.name, id: "shared" },
        ],
      },
    ],
    assertions: [
      {
        expected: true,
        fact: {
          name: "allow",
          args: [
            { tag: "User", id: "alice" },
            permission,
            { tag: resource.name, id: "shared" },
          ],
        },
      },
    ],
  };
}

export function shareWithParentTest(resource: Resource): TestBlock | null {
  const { parent, configuration } = resource.sharing.canShareWithParent[0];
  if (configuration.length === 0) return null;
  const { parentRole, thisRole } = configuration[0];
  const implication = resource.rolePermissionImplications.find(
    (implication) => implication.role === thisRole
  );
  if (implication === undefined) return null;
  return {
    name: `${resource.name} - Sharing with parent`,
    setup: [
      {
        name: "has_relation",
        args: [
          { tag: resource.name, id: "shared" },
          toSnakeCase(parent),
          { tag: parent, id: "parent" },
        ],
      },
      {
        name: "has_role",
        args: [
          { tag: "User", id: "alice" },
          parentRole,
          { tag: parent, id: "parent" },
        ],
      },
      {
        comment: `# the "is_shared_with_parent" attribute indicates that this ${resource.name} is shared with its parent`,
        name: "is_shared_with_parent",
        args: [{ tag: resource.name, id: "shared" }],
      },
    ],
    assertions: [
      {
        expected: true,
        fact: {
          name: "allow",
          args: [
            { tag: "User", id: "alice" },
            implication.permission,
            { tag: resource.name, id: "shared" },
          ],
        },
      },
    ],
  };
}

export function sharePublicTest(resource: Resource): TestBlock | null {
  if (
    !resource.sharing.canSharePublic ||
    resource.sharing.canSharePublic.length === 0
  )
    return null;
  return {
    name: `${resource.name} - Sharing publicly`,
    setup: [
      {
        comment: `# the "is_public" attribute indicates that this ${resource.name} is shared publicly`,
        name: "is_public",
        args: [{ tag: resource.name, id: "shared" }],
      },
    ],
    assertions: [
      {
        expected: true,
        fact: {
          name: "allow",
          args: [
            { tag: "User", id: "alice" },
            resource.sharing.canSharePublic[0],
            { tag: resource.name, id: "shared" },
          ],
        },
      },
    ],
  };
}

function tests(state: State): string[] {
  function sharingTests(resource: Resource): string[] {
    const tests = [];
    if (resource.sharing.canShareWithOtherUsers) {
      tests.push(testToString(shareWithOtherUsersTest(resource)));
    }
    if (resource.sharing.canShareWithParent.length > 0) {
      tests.push(testToString(shareWithParentTest(resource)));
    }
    if (resource.sharing.canSharePublic) {
      tests.push(testToString(sharePublicTest(resource)));
    }
    return tests;
  }
  function resourceTests(resource: Resource): string[] {
    return [
      testToString(roleInheritanceTest(state, resource)),
      ...sharingTests(resource),
    ];
  }
  return state.resources.flatMap(resourceTests);
}

export function stateToPolar(state: State): string {
  function resourceToPolar({
    name,
    roles,
    permissions,
    relations,
    rolePermissionImplications,
    sharing,
  }: Resource): string {
    const bodyLines = [
      `roles = [${roles.map((role) => `"${role}"`).join(", ")}];`,
      `permissions = [${permissions
        .map((permission) => `"${permission}"`)
        .join(", ")}];`,
    ];
    if (relations.length > 0) {
      bodyLines.push(
        `relations = { ${relations
          .map(
            (relation) =>
              `${toSnakeCase(relation.resource)}: ${relation.resource}`
          )
          .join(", ")} };`
      );
    }
    for (const relation of relations) {
      bodyLines.push("");
      if (relation.roleInheritance.kind === "simple") {
        bodyLines.push(`role if role on "${toSnakeCase(relation.resource)}";`);
      } else {
        bodyLines.push(
          ...relation.roleInheritance.configuration.map(
            ({ thisRole, parentRole }) =>
              `"${thisRole}" if "${parentRole}" on "${toSnakeCase(
                relation.resource
              )}";`
          )
        );
      }
    }
    if (rolePermissionImplications.length > 0) {
      bodyLines.push("");
      bodyLines.push(
        ...rolePermissionImplications.map(
          ({ permission, role }) => `"${permission}" if "${role}";`
        )
      );
    }
    if (sharing.canSharePublic) {
      bodyLines.push("");
      for (const publicPermission of sharing.canSharePublic) {
        bodyLines.push(`"${publicPermission}" if is_public(resource);`);
      }
    }
    const resourceBlock = `resource ${name} {\n${bodyLines
      .map((line) => `  ${line}`)
      .join("\n")}\n}`;
    const rules: string[] = [];
    for (const { parent, configuration } of sharing.canShareWithParent) {
      for (const { parentRole, thisRole } of configuration) {
        rules.push(
          [
            `has_role(sharedToUser: Actor, "${thisRole}", shareableResource: ${name}) if`,
            `  parent matches ${parent} and`,
            `  has_relation(shareableResource, "${toSnakeCase(
              parent
            )}", parent) and`,
            `  has_role(sharedToUser, "${parentRole}", parent) and`,
            `  is_shared_with_parent(shareableResource);`,
          ].join("\n")
        );
      }
    }
    return [resourceBlock, ...rules].join("\n\n");
  }
  return [
    "actor User {}",
    ...state.resources.map(resourceToPolar),
    ...tests(state),
  ].join("\n\n");
}
